import { http } from '@pendo/http';
import { createEventPropertiesFromSegmentRules, getEventPropertyFilters } from '@utils/segments.js';

export const segmentsApi = '/api/s/_SID_/segment';

function hasRules ({ segment, rule } = {}) {
    const { definition } = segment;
    const filters = definition.filters.reduce((set, filter) => {
        if (filter.or) {
            filter.or.forEach((filter) => set.add(filter.kind));
        } else {
            set.add(filter.kind);
        }

        return set;
    }, new Set());

    return filters.has(rule);
}

export function hasVisitorRules (segment) {
    const { definition } = segment;

    return !definition.filters.length || hasRules({ segment, rule: 'visitor' });
}

export function hasAccountRules (segment) {
    return hasRules({ segment, rule: 'account' });
}

export function hasParentAccountRules (segment) {
    return hasRules({ segment, rule: 'parentAccount' });
}

export async function fetchSegment (id) {
    return http.get(`${segmentsApi}/${id}`).then((res) => res.data);
}

export async function fetchSegments () {
    return http.get(segmentsApi).then((res) => res.data);
}

export async function createSegment (segment) {
    return http.post(segmentsApi, segment).then((res) => res.data);
}

export async function updateSegment (segment) {
    return http.put(`${segmentsApi}/${segment.id}`, segment).then((res) => res.data);
}

export async function deleteSegment (segment) {
    return http.delete(`${segmentsApi}/${segment.id}`);
}

export async function getSegmentsCreatedByMe () {
    return http.get(segmentsApi, { params: { createdByMe: true } }).then((res) => res.data);
}

export const isNestedSegment = (segment) => !!segment?.compound;

export function filtersHaveRulesOfKind (filters = [], kind) {
    return filters.some((filter) => (filter.or ? filter.or.some((rule) => rule.kind === kind) : filter.kind === kind));
}

export function sendSaveSegmentTrackEvents (savedSegment, options = {}) {
    const { action = 'create', location = '' } = options;
    const eps = createEventPropertiesFromSegmentRules(savedSegment);

    window.pendo.track('Save Segment', {
        action,
        ...(location ? { location } : {}),
        segmentId: savedSegment.id,
        isShared: savedSegment.shared,
        hasDescription: !!savedSegment.description,
        ...eps,
    });

    if (eps.hasFilterTypes.includes('historicalMetadata')) {
        const historicalFilters = getEventPropertyFilters(savedSegment, 'historicalMetadata').reduce(
            (acc, filter) => {
                acc.kinds.add(filter.eventPropertyKind);
                acc.groups.add(filter.eventPropertyGroup);

                return acc;
            },
            { kinds: new Set(), groups: new Set() },
        );

        window.pendo.track('Run Historical Metadata', {
            productAreas: 'Segment',
            hasSegment: false,
            hasFilter: eps.hasFilter,
            hasGroupBy: false,
            metadataKind: Array.from(historicalFilters.kinds)
                .sort()
                .toString(),
            metadataGroup: Array.from(historicalFilters.groups)
                .sort()
                .toString(),
        });
    }
}
